import React from 'react';
import { Drawer, Select, message } from 'antd';
import SideDrawerStyles from './SideDrawer.styles';
import { bindActionCreators } from 'redux';
import { toggleSideMenu } from './SideDrawer.actions';
import vehicleIcon from 'src/assets/icons/vehicle.svg';
import consignmentIcon from 'src/assets/icons/consignment.svg';
import assignedVehicleIcon from 'src/assets/icons/assignedVehicles.svg';
import assignedDriverIcon from 'src/assets/icons/assignedDrivers.svg';
import invoiceIcon from 'src/assets/icons/invoice.svg';
import logoutIcon from 'src/assets/icons/logoutIcon.svg';
import { DatabaseOutlined, KeyOutlined, CarOutlined, UserOutlined, QuestionCircleOutlined, AppstoreOutlined } from '@ant-design/icons';
import AuthHelper from 'src/auth/auth-helper';
import {
  CustomFrontendMenuEntry,
  findCustomFrontendApp,
  getCustomFrontendMenu,
} from 'src/components/custom-frontend/customFrontendManifest';
import { StylesProps, RouterProps } from 'src/library/types';
import {
  vehicleRequestsRoute,
  vehicleRequestsMMRoute,
  logoutRoute,
  consignmentsRoute,
  resetPasswordSideBarRoute,
  assignedVehiclesRoute,
  assignedDriversRoute,
  invoicesRoute,
  receivablesRoute,
  downloadRequestRoute,
  userManagementRoute,
  moveableAssetsRoute,
} from 'src/routing/routeConstants';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import { languageList } from 'src/utils/utils';
import ReceivablesIcon from 'src/assets/icons/receivables.svg';
import DownloadIcon from 'src/assets/icons/download.svg';
import { getRequiredInvoiceConfig } from 'src/components/pages/Invoices/Invoices.utils';

const getStoredPermissions = (): string[] | null => {
  try {
    const storedPermissions = window.localStorage.getItem('permissions');
    if (!storedPermissions) {
      return null;
    }
    const parsedPermissions = JSON.parse(storedPermissions);
    return Array.isArray(parsedPermissions) ? parsedPermissions : null;
  } catch (error) {
    return null;
  }
};

const hasPermission = (permissions: string[] | null, permission: string) => {
  if (!permission) {
    return true;
  }
  if (!permissions) {
    return true;
  }
  return permissions.includes(permission);
};

const openCustomFrontendNewTab = (entry: CustomFrontendMenuEntry) => {
  const app = findCustomFrontendApp(entry.appId);
  if (!app) {
    message.error('Unable to launch Custom Frontend');
    return;
  }
  // Same kebab JSON this portal already emits in CREDENTIALS_SHARING —
  // custom_frontend's transporter_portal payloadSchema is shared between
  // transports. Nulls are dropped (raw localStorage.getItem misses).
  const headers = AuthHelper.getAuthenticationHeaders();
  const payload: Record<string, string> = {};
  Object.entries(headers).forEach(([key, value]) => {
    if (value) payload[key] = value;
  });
  // Creds ride in the URL hash (not sent to server / not in Referer).
  // Custom-frontend reads the hash on load, persists, then strips it.
  const auth = window.btoa(JSON.stringify(payload))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  // `?source=transporter_portal` identifies this host to the embedded SPA's
  // multi-host bootstrap; without it the SPA throws SourceMissingError.
  const target = entry.iframeRoute && entry.iframeRoute !== '/'
    ? `${app.url}${entry.iframeRoute}?source=transporter_portal#auth=${auth}`
    : `${app.url}/?source=transporter_portal#auth=${auth}`;
  window.open(target, '_blank', 'noopener,noreferrer');
};

interface IProps extends StylesProps<ReturnType<typeof SideDrawerStyles>>, RouterProps {
  toggleSideMenu: () => void;
  isSideMenuOpen: boolean;
  logoUrl: string;
  consignmentActionConfig: any;
  i18n;
  t;
  vendorConfig;
  vendorData;
  showReceivables;
}

const SideDrawer = (props: IProps): React.ReactElement => {
  const {
    classes,
    toggleSideMenu,
    isSideMenuOpen,
    history,
    logoUrl,
    consignmentActionConfig,
    i18n,
    t,
    vendorConfig,
    vendorData,
    showReceivables,
  } = props;

  const languageConfig = vendorConfig?.language_config;
  const allowedLanguages = languageConfig?.allowed_language_list || [];
  const isLanguageChangeAllowed = !!allowedLanguages?.length;
  const finalLanguageList = languageList.filter((item) => allowedLanguages.includes(item.key));
  const helpdeskUrl = vendorConfig?.vendor_portal_helpdesk_url;
  const showVehicleRequestsMM = vendorConfig?.show_vehicle_requests_mm || false;
  const requiredConfig = getRequiredInvoiceConfig(
    consignmentActionConfig?.flow_config,
    { vendorData },
  );
  const showConsignmentInvoice = requiredConfig?.show_consignment_invoice || false;
  const showMoveableAssets = requiredConfig?.show_moveable_assets || false;
  const permissions = getStoredPermissions();

  const shouldShowVehicleRequestsFLM = hasPermission(permissions, 'vehicle_request_flm');
  const shouldShowVehicleRequestsMM = showVehicleRequestsMM && hasPermission(permissions, 'vehicle_request_mm');
  const shouldShowConsignments = hasPermission(permissions, 'consignments');
  const shouldShowReceivables = showReceivables && hasPermission(permissions, 'receivables');
  const shouldShowInvoices = showConsignmentInvoice && hasPermission(permissions, 'invoices');
  const shouldShowAssignedVehicles = hasPermission(permissions, 'assigned_vehicles_drivers');
  const shouldShowAssignedDrivers = hasPermission(permissions, 'assigned_vehicles_drivers');
  const shouldShowMoveableAssets = showMoveableAssets && hasPermission(permissions, 'assigned_vehicles_drivers');
  const shouldShowAnalytics = hasPermission(permissions, 'analytics');
  const shouldShowUserManagement = hasPermission(permissions, 'user_management');

  const handleLanguageChange = (val: any) => {
    i18n.changeLanguage(val, (err: any) => {
      if (err) {
        message.error(err);
        return;
      }
      message.success('Language changed successfully');
      window.location.reload();
    });
  };

  return (
    <Drawer
      className={classes.drawer}
      visible={isSideMenuOpen}
      onClose={() => toggleSideMenu()}
      placement="left"
      width={392}
      closable={false}
      title={
        <div>
          <img src={logoUrl} alt="Logo" style={{ maxWidth: '250px', height: '50px' }} />
          <div className={classes.vendorDetails}>
            {window.localStorage.vendorName && (
              <div>{window.localStorage.vendorName}</div>
            )}

            {window.localStorage.vendorEmail && (
              <div>{window.localStorage.vendorEmail}</div>
            )}
          </div>
        </div>
      }
    >
      {shouldShowVehicleRequestsFLM && (
        <div className={classes.flexCenter}>
          <img src={vehicleIcon} alt="" />
          <span
            className={classes.moduleName}
            onClick={() => {
              history.push(vehicleRequestsRoute);
              toggleSideMenu();
            }}
          >
            {t('Vehicle Requests FLM')}
          </span>
        </div>
      )}
      {shouldShowVehicleRequestsMM &&
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <img src={vehicleIcon} alt="" />
          <span
            className={classes.moduleName}
            onClick={() => {
              history.push(vehicleRequestsMMRoute);
              toggleSideMenu();
            }}
          >
            {t('Vehicle Requests MM')}
          </span>
        </div>}
      {shouldShowConsignments && (
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <img src={consignmentIcon} alt="consignments" />
          <span
            className={classes.moduleName}
            onClick={() => {
              history.push(consignmentsRoute);
              toggleSideMenu();
            }}
          >
            {t('Consignments')}
          </span>
        </div>
      )}
      {shouldShowReceivables && (
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <img src={ReceivablesIcon} alt="receivables" />
          <span
            className={classes.moduleName}
            onClick={() => {
              history.push(receivablesRoute);
              toggleSideMenu();
            }}
          >
            {t('Receivables')}
          </span>
        </div>
      )}
      {shouldShowInvoices && (
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <img src={invoiceIcon} alt="invoices" />
          <span
            className={classes.moduleName}
            onClick={() => {
              history.push(invoicesRoute);
              toggleSideMenu();
            }}
          >
            {t('Approvals')}
          </span>
        </div>
      )}
      {shouldShowAssignedVehicles && (
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <img src={assignedVehicleIcon} alt="" />
          <span
            className={classes.moduleName}
            onClick={() => {
              history.push(assignedVehiclesRoute);
              toggleSideMenu();
            }}
          >
            {t('Assigned Vehicles')}
          </span>
        </div>
      )}

      {shouldShowAssignedDrivers && (
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <img src={assignedDriverIcon} alt="" />
          <span
            className={classes.moduleName}
            onClick={() => {
              history.push(assignedDriversRoute);
              toggleSideMenu();
            }}
          >
            {t('Assigned Drivers')}
          </span>
        </div>
      )}
      <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
        <img src={DownloadIcon} alt="" />
        <span
          className={classes.moduleName}
          onClick={() => {
            history.push(downloadRequestRoute);
            toggleSideMenu();
          }}
        >
          {t('Download Requests')}
        </span>
      </div>

      {shouldShowMoveableAssets && (
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <CarOutlined />
          <span
            className={classes.moduleName}
            onClick={() => {
              history.push(moveableAssetsRoute);
              toggleSideMenu();
            }}
          >
            {t('Moveable Assets')}
          </span>
        </div>
      )}

      {shouldShowAnalytics && (
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <DatabaseOutlined />
          <span
            className={classes.moduleName}
          >
            <a href="/analytics" style={{ color: 'inherit', textDecoration: 'none' }}>
              {t('Analytics')}
            </a>
          </span>
        </div>
      )}

      {shouldShowUserManagement && (
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <UserOutlined />
          <span
            className={classes.moduleName}
            onClick={() => {
              history.push(userManagementRoute);
              toggleSideMenu();
            }}
          >
            {t('User Management')}
          </span>
        </div>
      )}
      {getCustomFrontendMenu().map((m) => (
        <div key={`custom:${m.id}`} className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <AppstoreOutlined />
          <span
            className={classes.moduleName}
            onClick={() => {
              if (m.target === 'inApp') {
                history.push(m.iframeRoute);
              } else {
                openCustomFrontendNewTab(m);
              }
              toggleSideMenu();
            }}
          >
            {t(m.label)}
          </span>
        </div>
      ))}
      {helpdeskUrl && (
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <QuestionCircleOutlined />
          <span
            className={classes.moduleName}
            onClick={() => {
              window.open(helpdeskUrl, '_blank', 'noopener,noreferrer');
              toggleSideMenu();
            }}
          >
            {t('Help Desk')}
          </span>
        </div>
      )}
      <div className={classes.menu} style={{ position: 'absolute', bottom: 40 }}>
        {isLanguageChangeAllowed &&
          <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
            <Select
              placeholder="Change Language"
              defaultValue={i18n.language}
              onChange={handleLanguageChange}
              style={{
                width: '150px',
                borderRadius: '4px',
              }}
            >
              {finalLanguageList?.map((item: any) => {
                return (
                  <Select.Option
                    key={item.key}
                    value={item.key}
                  >
                    {item.language_name}
                  </Select.Option>
                );
              })}
            </Select>
          </div>}
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <KeyOutlined />
          <span
            className={classes.moduleName}
            onClick={() => {
              history.push(resetPasswordSideBarRoute);
              toggleSideMenu();
            }}
          >
            {t('Reset Password')}
          </span>
        </div>
        <div className={classes.flexCenter} style={{ marginTop: '10px' }}>
          <img src={logoutIcon} alt="" />
          <span
            className={classes.moduleName}
            style={{ fontWeight: 'normal' }}
            onClick={() => {
              history.push(logoutRoute);
            }}
          >
            {t('Logout')}
          </span>
        </div>
      </div>
    </Drawer>
  );
};

const mapStateToProps = (state) => {
  const {
    logo_url,
    vendor_config,
    vendor_data,
    show_receivables,
  } = state.masterData;
  return {
    isSideMenuOpen: state.SideDrawerReducer.isSideMenuOpen,
    logoUrl: logo_url,
    consignmentActionConfig: vendor_config?.consignment_action_config,
    vendorConfig: vendor_config,
    vendorData: vendor_data,
    showReceivables: show_receivables,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return bindActionCreators({
    toggleSideMenu,
  }, dispatch);
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: SideDrawerStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(SideDrawer);
